import { axios } from "../config/axios";
import { IAxiosResponse } from "../interfaces/IAxiosResponse";
import {
  EsimLikeForLikeParams,
  EsimMarginParams,
  EsimRealizedParams,
  IEsimCompetitorComparisonReport,
  IEsimCountryMarginReport,
  IEsimLikeForLikeReport,
  IEsimRealizedMarginReport,
  IEsimTariffSnapshot
} from "../interfaces/esim/EsimMargin.types";
import { serialize } from "../utils/serializeToQueryString";

// Both answer 404 with succeeded: false until the daily margin job has written its first snapshot,
// or when no snapshot exists on or before the day asked for. The interceptor rejects on 404, so callers
// read error.response.status rather than the envelope.
const query = (params: EsimMarginParams): string =>
  serialize({
    date: params.date ?? "",
    countryIso2: params.countryIso2 ?? "",
    locationZoneId: params.locationZoneId ?? 0
  });

export const getEsimMarginCountries = async (
  params: EsimMarginParams
): Promise<IAxiosResponse<IEsimCountryMarginReport>> =>
  axios.get(`v1/EsimMargin/Countries${query(params)}`);

export const getEsimMarginTariff = async (
  params: EsimMarginParams
): Promise<IAxiosResponse<IEsimTariffSnapshot>> =>
  axios.get(`v1/EsimMargin/Tariff${query(params)}`);

// 404 until the competitor price job has run once. With a country, both sides' plan lists come too.
export const getEsimCompetitorComparison = async (
  params: EsimMarginParams
): Promise<IAxiosResponse<IEsimCompetitorComparisonReport>> =>
  axios.get(`v1/EsimMargin/Competitors${query(params)}`);

// Not a snapshot: computed from the sales and the usage rows on each call, so it never answers 404.
export const getEsimRealizedMargins = async (
  params: EsimRealizedParams
): Promise<IAxiosResponse<IEsimRealizedMarginReport>> =>
  axios.get(`v1/EsimMargin/Realized${serialize({ from: params.from, to: params.to })}`);

// Our packages in one destination against the cheapest competitor plan covering each one's data and
// days: the basket comparison. 404 until a competitor snapshot exists.
export const getEsimLikeForLike = async (
  params: EsimLikeForLikeParams
): Promise<IAxiosResponse<IEsimLikeForLikeReport>> =>
  axios.get(
    `v1/EsimMargin/LikeForLike${serialize({ date: params.date ?? "", countryIso2: params.countryIso2 })}`
  );
